import {
    countCaptureGroups,
    extractPlaceholders,
    findDuplicates,
    isObject,
} from './helpers';
import { RewriteRule, ValidationResult } from './types';

export const validateRewriteRuleConfig = (
    config: unknown
): ValidationResult => {
    const errors: string[] = [];

    if (!isObject(config)) {
        return { ok: false, errors: ['Config must be an object.'] };
    }

    const regex = (config as RewriteRule).regex;
    const groups = (config as RewriteRule).groups;
    const template = (config as RewriteRule).template;

    if (typeof regex !== 'string' || regex.trim().length === 0) {
        errors.push('regex must be a non-empty string.');
    }

    if (!Array.isArray(groups) || groups.some((g) => typeof g !== 'string')) {
        errors.push('groups must be an array of strings.');
    } else {
        if (groups.length === 0) errors.push('groups must not be empty.');
        if (groups.some((g) => g.trim().length === 0))
            errors.push('groups cannot contain empty names.');
        const dupes = findDuplicates(groups);
        if (dupes.length > 0)
            errors.push(`groups contains duplicates: ${dupes.join(', ')}`);
        if (groups.some((g) => !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(g))) {
            errors.push(
                'Each group name must match /^[a-zA-Z_][a-zA-Z0-9_]*$/.'
            );
        }
    }

    if (typeof template !== 'string' || template.trim().length === 0) {
        errors.push('template must be a non-empty string.');
    }

    if (typeof regex === 'string') {
        if (!regex.startsWith('^') || !regex.endsWith('$')) {
            errors.push('regex must be anchored with ^ at start and $ at end.');
        }
    }

    if (errors.length > 0) {
        return { ok: false, errors };
    }

    try {
        new RegExp(regex);
    } catch (e) {
        return {
            ok: false,
            errors: [`regex is not a valid RegExp: ${String(e)}`],
        };
    }

    const inferredCaptureCount = countCaptureGroups(regex);
    if (Array.isArray(groups) && inferredCaptureCount !== groups.length) {
        errors.push(
            `regex has ${inferredCaptureCount} capturing group(s), but groups has ${groups.length}.`
        );
    }

    const placeholders = extractPlaceholders(String(template));
    const unknown = placeholders.filter((p) => !groups.includes(p));
    if (unknown.length > 0) {
        errors.push(
            `template uses unknown placeholder(s): ${unknown.join(', ')}`
        );
    }

    if (errors.length > 0) {
        return { ok: false, errors };
    }

    return {
        ok: true,
        result: {
            regex,
            groups: [...groups],
            template,
        },
    };
};
